import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { PHONE_DISPLAY, CONTACT_EMAIL } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";

export const Footer = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-card">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Aadisarvtra logo" className="h-10 w-10 rounded-lg object-cover" />
            <span className="font-heading text-base font-bold">{t.brandFull}</span>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">{t.footer.tagline}</p>
          <div className="mt-4 space-y-2">
            <a href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`} data-testid="footer-phone-link" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <Phone className="h-4 w-4 text-rose-600" /> {PHONE_DISPLAY}
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-email-link" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <Mail className="h-4 w-4 text-rose-600" /> {CONTACT_EMAIL}
            </a>
          </div>
        </div>
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {t.footer.quickLinks}
          </h4>
          <div className="mt-4 flex flex-col gap-2.5">
            {[
              { key: "services", href: "#services" },
              { key: "products", href: "#products" },
              { key: "about", href: "#about" },
              { key: "contact", href: "#contact" },
            ].map((l) => (
              <a
                key={l.key}
                href={l.href}
                data-testid={`footer-link-${l.key}`}
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {t.nav[l.key]}
              </a>
            ))}
            <Link
              to="/careers"
              data-testid="footer-link-careers"
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t.nav.careers}
            </Link>
          </div>
        </div>
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {t.footer.offices}
          </h4>
          <div className="mt-4 space-y-4">
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <span><strong className="text-foreground">{t.about.headOffice}</strong><br />{t.about.headAddress}</span>
            </p>
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span><strong className="text-foreground">{t.about.branchOffice}</strong><br />{t.about.branchAddress}</span>
            </p>
          </div>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 sm:flex-row sm:px-6">
          <p className="text-xs text-muted-foreground">
            © {year} {t.brandFull}. {t.footer.rights}
          </p>
          <Link
            to="/admin/login"
            data-testid="footer-admin-login-link"
            className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.footer.adminLogin}
          </Link>
        </div>
      </div>
    </footer>
  );
};
